import { Grid, Paper } from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { ShiftForm } from '.';
import * as api from '../../api/app/shiftsApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import { useTranslation } from '../../contexts/translation';
import { Shift, ShiftInputs } from '../../types/app/shifts.types';
import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/shift.functions';
import { TimeFormatTypes } from '../../utils/core/functions/timeFormat.functions';
import { paths } from '../../utils/core/routes';
import { useStyles } from './ShiftForm.styles';

export function ShiftDetail() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const [defaultValues, setDefaultValues] = useState<ShiftInputs | null>(null);
  const { mutate: mutateUpdateShift } = useMutation(api.updateShift);
  const { data, isLoading, isError } = useQuery<Shift>(['shifts', id], () =>
    api.getShift(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      name: data.name,
      start: moment(data.start, 'HH:mm').toDate(),
      end: moment(data.end, 'HH:mm').toDate(),
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<ShiftInputs> = (data: ShiftInputs) => {
    if (!defaultValues) return;

    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      start: moment(data.start).format(TimeFormatTypes.HH_mm + ':00'),
      end: moment(data.end).format(TimeFormatTypes.HH_mm + ':00'),
      id: Number(id!),
    });

    mutateUpdateShift(
      { ...requestData, id: Number(id) },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.shift.item-edited'), { variant: 'success' });
          navigate(paths.SHIFTS_LIST);
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <PageHeader title={t('page.shift.headline-edit')} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8} lg={4}>
            <Paper className={classes.paper}>
              <ShiftForm
                onSubmit={handleOnSubmit}
                onBack={() => navigate(paths.SHIFTS_LIST)}
                defaultValues={defaultValues}
              />
            </Paper>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
